import CardChart from '../Components/CardChart'
import { Grid, Box, Typography, Chip, LinearProgress, Button, Skeleton, Divider } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import FormatDate from '../../../../../date/FormatDate'
import IconTooltipButton from '../../../../../button/IconTooltipButton'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import TextEllipsis from '../../../../../text/TextEllipsis'
import { fontSize } from '@mui/system'
import { useSelector } from 'react-redux'
import TextStatusUnitOfLearning from '../../../../../text/TextStatus'

const UnitsMarkingActivity = ({ unitsMarkingActivity = [], isLoading, statuses = [] }) => {
  const [filterStatus, setFilterStatus] = useState('all')
  const [data, setData] = useState(unitsMarkingActivity)
  const years = useSelector((state) => state?.tenant?.years || [])

  const onFilterStatus = (item) => {
    if (filterStatus === 'all') {
      return true
    }
    return item?.status?.label === filterStatus
  }

  useEffect(() => {
    const newData = unitsMarkingActivity?.filter(onFilterStatus)
    setData(newData)
  }, [unitsMarkingActivity, filterStatus])

  return (
    <CardChart title='Units Marking Activity' bgColor='#fff'>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexWrap: 'wrap',
          minWidth: { md: 260 },
          mb: 1
        }}
      >
        <Chip
          label={`
            All: ${unitsMarkingActivity?.length || 0}`}
          onClick={() => setFilterStatus('all')}
          icon={<FontAwesomeIcon icon={'user'} />}
          disabled={isLoading}
          sx={{
            fontWeight: 600,
            fontSize: '0.75rem',
            borderRadius: '999px',
            border: filterStatus === 'all' ? '2px solid currentColor' : 'none'
          }}
        />
        {statuses.map((s) => (
          <Chip
            key={s.id}
            label={`${s.label}: ${s.count || 0}`}
            onClick={() => setFilterStatus(s.label)}
            icon={<FontAwesomeIcon icon={s.icon} style={{ color: s.color }} />}
            sx={{
              backgroundColor: s.bgColor,
              color: s.color,
              fontWeight: 600,
              fontSize: '0.75rem',
              borderRadius: '999px',
              border: filterStatus === s.label ? '2px solid currentColor' : 'none'
            }}
          />
        ))}
      </Box>

      {/* MF-529 — minmax(0, 1fr) + min-width 0 down the card so a long unit name ellipsises instead
          of widening its column past the viewport */}
      <Box className='grid gap-4' sx={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
        {isLoading ? (
          Array.from({ length: 2 }).map((_, i) => (
            <Box key={i} sx={{ mb: 3 }}>
              <Skeleton height={100} width='100%' variant='rounded' />
            </Box>
          ))
        ) : data?.length === 0 ? (
          <Typography variant='h6' sx={{ color: '#6b7280', py: 2 }}>
            {" You don't have any unit of learning yet"}
          </Typography>
        ) : (
          data.map((unit) => (
            <Box key={`ul-${unit._id}-class-${unit.classId}`} sx={{ minWidth: 0 }}>
              <Box
                sx={{
                  border: `2px solid ${unit.status?.color || '#6b7280'}`,
                  borderRadius: '12px',
                  backgroundColor: unit.status?.bgColor || '#f1f1f1',
                  position: 'relative'
                }}
              >
                <Box sx={{ position: 'absolute', top: 0, left: 0, overflow: 'hide' }}>
                  <Box
                    sx={{
                      color: unit.status?.bgColor || '#f1f1f1',
                      backgroundColor: unit.status?.color || '#6b7280',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      px: 2,
                      borderTopLeftRadius: 5,
                      borderBottomRightRadius: 5
                    }}
                  >
                    <Typography>{unit.status?.label || '-'}</Typography>
                  </Box>
                </Box>
                <Box sx={{ position: 'absolute', top: 12, right: 12 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Link
                      to={`/dashboard/classes/${unit?.curriculumId}/preview/${unit?.classItem?._id}/unit-of-learning/${unit._id}`}
                    >
                      <IconTooltipButton
                        icon='arrow-right'
                        classNameIcon='text-2xl'
                        style={{
                          color: unit.status?.color || '#6b7280'
                        }}
                        title='View unit'
                      />
                    </Link>
                  </Box>
                </Box>
                <Box sx={{ pt: 3, pb: 1, px: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant='body2' sx={{ opacity: 0.6 }}>
                      {years
                        .filter((y) => unit?.yearLevel?.includes(y._id))
                        .map((y) => y.name)
                        .join(', ') || 'N/A'}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                      <TextStatusUnitOfLearning startDate={unit.startDate} endDate={unit.endDate} />
                      <TextEllipsis
                        style={{
                          fontWeight: 'bold',
                          fontSize: 16
                        }}
                      >
                        {unit.name}
                      </TextEllipsis>
                    </Box>
                    <Typography variant='body2' sx={{ opacity: 0.8 }}>
                      {unit.subject.map((s) => s.name)}
                    </Typography>
                  </Box>
                </Box>

                <Grid container spacing={2} sx={{ px: 2 }}>
                  <Grid item xs={4}>
                    <Typography variant='body2' sx={{ color: '#6b7280', fontSize: '0.8rem' }}>
                      Total Skills
                    </Typography>
                    <Typography variant='h4' fontWeight='700' sx={{ color: '#111827' }}>
                      {unit.totalSkills}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant='body2' sx={{ color: '#6b7280', fontSize: '0.8rem' }}>
                      Marked
                    </Typography>
                    <Typography variant='h4' fontWeight='700' sx={{ color: '#111827' }}>
                      {unit.markedSkills}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant='body2' sx={{ color: '#6b7280', fontSize: '0.8rem' }}>
                      Students
                    </Typography>
                    <Typography variant='h4' fontWeight='700' sx={{ color: '#111827' }}>
                      {unit.studentsCount}
                    </Typography>
                  </Grid>
                </Grid>
                <Box sx={{ px: 2, pb: 2 }}>
                  <LinearProgress
                    variant='determinate'
                    value={unit.coverage}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: 'var(--muted)',
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 4,
                        backgroundColor: unit.status?.color || '#6b7280'
                      }
                    }}
                  />
                  <Typography variant='body2' sx={{ color: '#6b7280', mt: 0.5 }}>
                    {unit.coverage}% coverage
                  </Typography>
                  <Typography variant='body2' sx={{ color: '#6b7280' }}>
                    Class code: {unit.classCode}
                  </Typography>
                </Box>
              </Box>
            </Box>
          ))
        )}
      </Box>
    </CardChart>
  )
}

export default UnitsMarkingActivity
